import {
  normalizeToProfilePointer,
  normalizeToEventPointer,
  normalizeToAddressPointer,
} from "applesauce-core/helpers";
import { use$ } from "applesauce-react/hooks";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { nip19 } from "nostr-tools";
import { useEffect, useMemo, useState } from "react";
import {
  catchError,
  combineLatest,
  endWith,
  filter,
  ignoreElements,
  map,
  of,
  startWith,
  tap,
} from "rxjs";
import { deletionEvents$, eventStore, pool } from "../services/nostr";
import {
  REPO_KIND,
  ISSUE_KIND,
  PATCH_KIND,
  PR_KIND,
  PR_UPDATE_KIND,
  getRootRepositoryCoordinates,
  type ResolvedRepo,
} from "../lib/nip34";
import {
  eventIdToNevent,
  isNip05,
  isHexPubkey,
  standardizeNip05,
} from "../lib/routeUtils";
import { resilientRequest } from "@/lib/resilientSubscription";
import { normalizeUrl } from "@/lib/url";
import { useRepoPath } from "../hooks/useRepoPath";
import UserPage from "./UserPage";
import NotFound from "./NotFound";
import { UnsupportedEventPage } from "./UnsupportedEventPage";
import { EventSearchStatus } from "@/components/EventSearchStatus";
import {
  useEventSearch,
  type RelayGroupSpec,
  type SearchTarget,
} from "@/hooks/useEventSearch";
import { gitIndexRelays, fallbackRelays } from "../services/settings";
import { useDnsIdentity } from "../hooks/useDnsIdentity";
import type { NostrEvent } from "nostr-tools";
import type { Observable } from "rxjs";
import type { Filter } from "applesauce-core/helpers";
import {
  getReplaceableIdentifier,
  parseReplaceableAddress,
} from "applesauce-core/helpers";
import { getNip10References } from "applesauce-common/helpers";
import {
  isValidSoftwareApplication,
  isValidSoftwareAsset,
  isValidSoftwareRelease,
  SOFTWARE_APPLICATION_KIND,
  SOFTWARE_ASSET_KIND,
  SOFTWARE_RELEASE_KIND,
} from "@/casts/Software";
import { ZAPSTORE_RELAY_URL } from "@/hooks/useSoftwareReleases";
import { RepositoryModel } from "@/models/RepositoryModel";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract root repository coordinates, preserving tag order. */
function getRepoCoords(event: NostrEvent): string[] {
  const seen = new Set<string>();
  const coords: string[] = [];

  for (const coord of getRootRepositoryCoordinates(event)) {
    if (!coord || seen.has(coord)) continue;
    const parsed = parseRepoCoord(coord);
    if (parsed?.kind !== REPO_KIND || !isHexPubkey(parsed.pubkey)) continue;
    seen.add(coord);
    coords.push(coord);
  }

  return coords;
}

/** Extract relay hints from repository `a` tags. */
function getRepoCoordRelayHints(event: NostrEvent): string[] {
  return dedupeRelays(
    event.tags
      .filter(([name, , , marker]) => name === "a" && marker !== "mention")
      .map(([, , relay]) => relay)
      .filter((relay): relay is string => !!relay),
  );
}

function dedupeRelays(relays: string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];

  for (const relay of relays) {
    const normalized = normalizeUrl(relay);
    if (seen.has(normalized)) continue;
    seen.add(normalized);
    unique.push(normalized);
  }

  return unique;
}

/**
 * Parse a repo coordinate string (kind:pubkey:dTag) into its parts.
 * Returns undefined if the coord is malformed.
 */
function parseRepoCoord(
  coord: string,
): { kind: number; pubkey: string; dTag: string } | undefined {
  const parts = coord.split(":");
  if (parts.length < 3) return undefined;
  const kind = parseInt(parts[0], 10);
  const pubkey = parts[1];
  const dTag = parts.slice(2).join(":");
  if (isNaN(kind) || !pubkey || !dTag) return undefined;
  return { kind, pubkey, dTag };
}

function getRepoCoordFilters(coords: string[]): Filter[] {
  return coords
    .map(parseRepoCoord)
    .filter(
      (coord): coord is { kind: number; pubkey: string; dTag: string } =>
        !!coord && coord.kind === REPO_KIND && isHexPubkey(coord.pubkey),
    )
    .map(
      ({ pubkey, dTag }) =>
        ({
          kinds: [REPO_KIND],
          authors: [pubkey],
          "#d": [dTag],
          limit: 1,
        }) as Filter,
    );
}

/**
 * Redirect component that resolves a repo coordinate to a path (with NIP-05
 * preference) and navigates to the target sub-path.
 *
 * Using a component lets us call the useRepoPath hook after the event is loaded.
 */
function RepoCoordRedirect({
  coord,
  hintRelays,
  subPath,
  stargazerPubkey,
}: {
  coord: string;
  hintRelays: string[];
  /** Path segment appended after the repo root, e.g. "/issues/nevent1..." */
  subPath: string;
  /** When set, appends ?stargazer=<pubkey> to open the stargazers popover. */
  stargazerPubkey?: string;
}) {
  const parsed = parseRepoCoord(coord);
  if (!parsed) return <NotFound />;
  return (
    <RepoPathRedirect
      pubkey={parsed.pubkey}
      repoId={parsed.dTag}
      relays={hintRelays}
      subPath={subPath}
      stargazerPubkey={stargazerPubkey}
    />
  );
}

function RepoCoordsRedirect({
  coords,
  hintRelays,
  subPath,
  stargazerPubkey,
}: {
  coords: string[];
  hintRelays: string[];
  /** Path segment appended after the repo root, e.g. "/issues/nevent1..." */
  subPath: string;
  /** When set, appends ?stargazer=<pubkey> to open the stargazers popover. */
  stargazerPubkey?: string;
}) {
  const coordsKey = coords.join("\u0000");
  const relaysKey = hintRelays.join("\u0000");
  const candidateCoords = useMemo(() => {
    const seen = new Set<string>();
    return coords.filter((coord) => {
      if (seen.has(coord)) return false;
      const parsed = parseRepoCoord(coord);
      if (parsed?.kind !== REPO_KIND || !isHexPubkey(parsed.pubkey)) {
        return false;
      }
      seen.add(coord);
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coordsKey]);

  // Single pointer: exact-coordinate lookup (authors-scoped fast path from
  // b19fba4b). Multi-pointer: ONE identifier-only wave covering the whole
  // pointer set — a relay holding any of these repositories returns every
  // announcement for their identifiers in a single round trip, replacing the
  // former per-pointer sequential settlement stages. Authority is never
  // derived from this fetch (AGENTS.md §Repository authorization model
  // carve-out): each pointer is compared through reciprocal resolution below.
  const filters = useMemo<Filter[]>(() => {
    if (candidateCoords.length <= 1) {
      return getRepoCoordFilters(candidateCoords);
    }
    const dTags = [
      ...new Set(
        candidateCoords.flatMap((coord) => {
          const parsed = parseRepoCoord(coord);
          return parsed ? [parsed.dTag] : [];
        }),
      ),
    ];
    return dTags.length > 0
      ? [{ kinds: [REPO_KIND], "#d": dTags } as Filter]
      : [];
  }, [candidateCoords]);

  const lookupComplete =
    use$(() => {
      if (candidateCoords.length === 0 || filters.length === 0) return of(true);

      const relays = dedupeRelays([
        ...hintRelays,
        ...gitIndexRelays.getValue(),
        ...fallbackRelays.getValue(),
      ]);

      if (relays.length === 0) return of(true);

      return resilientRequest(pool, relays, filters).pipe(
        tap((response: NostrEvent | "EOSE") => {
          if (response !== "EOSE") eventStore.add(response);
        }),
        filter((response) => response === "EOSE"),
        map(() => true),
        startWith(false),
        catchError(() => of(true)),
      ) as Observable<boolean>;
    }, [coordsKey, relaysKey]) ?? false;

  const componentResolution = use$(() => {
    if (candidateCoords.length === 0 || filters.length === 0) {
      return of({ settled: true, coordinate: undefined });
    }

    const pointers = candidateCoords.flatMap((coordinate) => {
      const parsed = parseRepoCoord(coordinate);
      return parsed ? [{ coordinate, ...parsed }] : [];
    });
    // A single repository pointer is unambiguous. Once the exact-coordinate
    // lookup above finishes, route through its current deletion-aware model
    // without adding two component-settlement network stages. Multi-pointer
    // items still need component comparison before choosing a destination.
    if (pointers.length === 1) {
      const pointer = pointers[0];
      return (
        eventStore.model(
          RepositoryModel,
          pointer.pubkey,
          pointer.dTag,
          deletionEvents$,
        ) as unknown as Observable<ResolvedRepo | undefined>
      ).pipe(
        map((repository) => ({
          settled: true,
          coordinate: repository ? pointer.coordinate : undefined,
        })),
      );
    }

    // Multi-pointer: compare each pointer's reciprocal component from the
    // store. The relay work is the single identifier-only wave above
    // (lookupComplete); these deletion-aware models are pure store
    // projections, so the comparison itself is always concluded here and the
    // redirect / ambiguity refusal below act on it only once the wave has
    // settled on every relay — completeness claims need full coverage.
    return combineLatest(
      pointers.map(
        ({ pubkey, dTag }) =>
          eventStore.model(
            RepositoryModel,
            pubkey,
            dTag,
            deletionEvents$,
          ) as unknown as Observable<ResolvedRepo | undefined>,
      ),
    ).pipe(
      map((repositories) => {
        const components = new Map(
          repositories.flatMap((repository) =>
            repository ? [[repository.componentId, repository] as const] : [],
          ),
        );
        // A root item naming unrelated repository components is ambiguous.
        // Refuse instead of granting tag order authority over the route.
        if (components.size !== 1) {
          return { settled: true, coordinate: undefined };
        }
        const repository = [...components.values()][0];
        const canonical = pointers.find(
          ({ coordinate }) => coordinate === repository.selectedCoordinate,
        )?.coordinate;
        const fallback = pointers.find(
          (_, index) =>
            repositories[index]?.componentId === repository.componentId,
        )?.coordinate;
        return { settled: true, coordinate: canonical ?? fallback };
      }),
    );
  }, [coordsKey]);

  const redirectCoordinate = componentResolution?.settled
    ? componentResolution.coordinate
    : undefined;

  if (redirectCoordinate && lookupComplete) {
    return (
      <RepoCoordRedirect
        coord={redirectCoordinate}
        hintRelays={hintRelays}
        subPath={subPath}
        stargazerPubkey={stargazerPubkey}
      />
    );
  }

  if (lookupComplete && componentResolution?.settled) return <NotFound />;
  return <LoadingState message="Resolving repository…" />;
}

/**
 * Inner redirect: calls useRepoPath (which may prefer NIP-05) then navigates.
 *
 * Forwards the current location's search params and hash to the destination
 * so that ?unread=... and #anchor fragments survive the redirect chain.
 * The ?stargazer= param takes precedence over any forwarded search string.
 */
function RepoPathRedirect({
  pubkey,
  repoId,
  relays,
  subPath,
  stargazerPubkey,
}: {
  pubkey: string;
  repoId: string;
  relays: string[];
  subPath: string;
  /** When set, appends ?stargazer=<pubkey> to open the stargazers popover. */
  stargazerPubkey?: string;
}) {
  const repoPath = useRepoPath(pubkey, repoId, relays);
  const location = useLocation();

  // Build the search string: stargazer takes precedence; otherwise forward
  // whatever search params arrived on the current URL (e.g. ?unread=...).
  let search = "";
  if (stargazerPubkey) {
    search = `?stargazer=${stargazerPubkey}`;
  } else if (location.search) {
    search = location.search;
  }

  // Forward the hash fragment too (e.g. #<anchorId> for comment permalinks).
  // subPath may already contain a fragment (e.g. "/issues/nevent1...#abc123");
  // only append location.hash if subPath doesn't already have one.
  const subPathHasHash = subPath.includes("#");
  const hash = !subPathHasHash && location.hash ? location.hash : "";

  return <Navigate to={`${repoPath}${subPath}${search}${hash}`} replace />;
}

/** Get the uppercase `E` root tag from a NIP-22 comment (kind 1111). */
function getCommentRootId(event: NostrEvent): string | undefined {
  return event.tags.find(([t]) => t === "E")?.[1];
}

/**
 * Get the NIP-10 thread root event ID from any event that uses `e` tags for
 * threading (kind:1 replies, legacy NIP-34 replies, etc.).
 *
 * Prefers the marked `root` tag; falls back to the first positional `e` tag
 * for older events that don't use markers.
 */
function getNip10RootId(event: NostrEvent): string | undefined {
  const refs = getNip10References(event);
  // Marked root takes priority
  if (refs.root?.e?.id) return refs.root.e.id;
  // Positional fallback: first e tag is the root when there are multiple
  const eTags = event.tags.filter(([t]) => t === "e");
  if (eTags.length >= 2) return eTags[0]?.[1];
  // Single e tag — this event is a direct reply to that event
  if (eTags.length === 1) return eTags[0]?.[1];
  return undefined;
}

interface SoftwareApplicationPointer {
  kind: typeof SOFTWARE_APPLICATION_KIND;
  pubkey: string;
  dTag: string;
  relayHints: string[];
}

function getSoftwareApplicationPointer(
  release: NostrEvent,
): SoftwareApplicationPointer | undefined {
  const appId = release.tags.find(([name]) => name === "i")?.[1];
  if (!appId) return undefined;

  for (const [name, address, relayHint] of release.tags) {
    if (name !== "a" || !address) continue;
    const pointer = parseReplaceableAddress(address, true);
    if (
      pointer?.kind === SOFTWARE_APPLICATION_KIND &&
      pointer.identifier === appId
    ) {
      return {
        kind: SOFTWARE_APPLICATION_KIND,
        pubkey: pointer.pubkey.toLowerCase(),
        dTag: pointer.identifier,
        relayHints: relayHint ? [relayHint] : [],
      };
    }
  }

  // Compatibility with existing Zapstore releases that identify the
  // application only by `i`; their application is published by the same key.
  return {
    kind: SOFTWARE_APPLICATION_KIND,
    pubkey: release.pubkey,
    dTag: appId,
    relayHints: [],
  };
}

function SoftwareApplicationRedirect({
  application,
  hintRelays,
}: {
  application: NostrEvent;
  hintRelays: string[];
}) {
  if (!isValidSoftwareApplication(application)) return <NotFound />;
  const coords = getRepoCoords(application);
  if (coords.length === 0) return <NotFound />;
  const repoRelays = dedupeRelays([
    ...getRepoCoordRelayHints(application),
    ...hintRelays,
  ]);
  const applicationNevent = eventIdToNevent(application.id, hintRelays);
  return (
    <RepoCoordsRedirect
      coords={coords}
      hintRelays={repoRelays}
      subPath={`/releases/apps/${applicationNevent}`}
    />
  );
}

function SoftwareReleaseRedirect({
  release,
  hintRelays,
  assetId,
}: {
  release: NostrEvent;
  hintRelays: string[];
  assetId?: string;
}) {
  const pointer = useMemo(
    () => getSoftwareApplicationPointer(release),
    [release],
  );
  const pointerKey = pointer
    ? `${pointer.kind}:${pointer.pubkey}:${pointer.dTag}`
    : "";
  const relaysKey = hintRelays.join("\u0000");
  const searchGroups = useMemo<RelayGroupSpec[]>(() => {
    const applicationRelays = dedupeRelays([
      ...(pointer?.relayHints ?? []),
      ...hintRelays,
      ZAPSTORE_RELAY_URL,
    ]);
    return [
      { label: "release relays", relays$: of(applicationRelays) },
      { label: "git index", relays$: gitIndexRelays },
      {
        label: "fallback relays",
        relays$: fallbackRelays,
        deferred: true,
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointerKey, relaysKey]);
  const target = useMemo<SearchTarget | undefined>(
    () =>
      pointer
        ? {
            type: "address",
            kind: pointer.kind,
            pubkey: pointer.pubkey,
            dTag: pointer.dTag,
          }
        : undefined,
    [pointer],
  );
  const search = useEventSearch(target, searchGroups);
  const application = search?.event;

  if (!pointer || !isValidSoftwareRelease(release)) return <NotFound />;

  if (application && isValidSoftwareApplication(application)) {
    const coords = getRepoCoords(application);
    if (coords.length === 0) return <NotFound />;
    const repoRelays = dedupeRelays([
      ...getRepoCoordRelayHints(application),
      ...pointer.relayHints,
      ...hintRelays,
    ]);
    const releaseNevent = eventIdToNevent(release.id, hintRelays);
    const fragment = assetId ? `#${assetId.slice(0, 15)}` : "";
    return (
      <RepoCoordsRedirect
        coords={coords}
        hintRelays={repoRelays}
        subPath={`/releases/${releaseNevent}${fragment}`}
      />
    );
  }

  if (search?.concludedNotFound) return <NotFound />;
  return <LoadingState message="Resolving software application…" />;
}

function SoftwareAssetRedirect({
  asset,
  hintRelays,
}: {
  asset: NostrEvent;
  hintRelays: string[];
}) {
  const appId = asset.tags.find(([name]) => name === "i")?.[1];
  const version = asset.tags.find(([name]) => name === "version")?.[1];
  const relaysKey = hintRelays.join("\u0000");
  const releaseFilter = useMemo(
    () =>
      ({
        kinds: [SOFTWARE_RELEASE_KIND],
        "#e": [asset.id],
      }) as Filter,
    [asset.id],
  );
  const lookupComplete =
    use$(() => {
      const relays = dedupeRelays([
        ...hintRelays,
        ZAPSTORE_RELAY_URL,
        ...gitIndexRelays.getValue(),
        ...fallbackRelays.getValue(),
      ]);
      if (relays.length === 0) return of(true);

      return resilientRequest(pool, relays, [releaseFilter]).pipe(
        tap((response: NostrEvent | "EOSE") => {
          if (response !== "EOSE") eventStore.add(response);
        }),
        ignoreElements(),
        endWith(true),
        startWith(false),
        catchError(() => of(true)),
      );
    }, [asset.id, relaysKey]) ?? false;
  const releases =
    use$(
      () =>
        eventStore
          .timeline([releaseFilter])
          .pipe(
            map((events) =>
              (events as unknown as NostrEvent[])
                .filter(
                  (event) =>
                    isValidSoftwareRelease(event) &&
                    event.tags.some(
                      ([name, value]) => name === "i" && value === appId,
                    ) &&
                    event.tags.some(
                      ([name, value]) =>
                        name === "version" && value === version,
                    ),
                )
                .sort(
                  (left, right) =>
                    right.created_at - left.created_at ||
                    right.id.localeCompare(left.id),
                ),
            ),
          ),
      [asset.id, appId, version],
    ) ?? [];
  const release = releases[0];

  if (!isValidSoftwareAsset(asset)) return <NotFound />;
  if (release) {
    return (
      <SoftwareReleaseRedirect
        release={release}
        hintRelays={hintRelays}
        assetId={asset.id}
      />
    );
  }
  if (lookupComplete) return <NotFound />;
  return <LoadingState message="Finding the release for this asset…" />;
}

// ---------------------------------------------------------------------------
// Loading spinner
// ---------------------------------------------------------------------------

function LoadingState({ message }: { message: string }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setVisible(true), 1000);
    return () => clearTimeout(id);
  }, []);
  return (
    <div
      className={`min-h-[50vh] flex items-center justify-center transition-opacity duration-500 ${visible ? "opacity-100" : "opacity-0"}`}
    >
      <div className="text-center space-y-3">
        <div className="h-8 w-8 border-2 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Event redirect — resolves an event and redirects to the right page
// ---------------------------------------------------------------------------

function EventRedirect({
  eventId,
  hintRelays,
  authorPubkey,
  commentId,
  stargazerPubkey,
  originalEvent,
}: {
  eventId: string;
  hintRelays: string[];
  /** Author pubkey if known (from nevent). Enables vanish check. */
  authorPubkey?: string;
  /** If set, this is a comment permalink — we're resolving the root event. */
  commentId?: string;
  /** If set, this is a star reaction permalink — open the stargazers popover. */
  stargazerPubkey?: string;
  /**
   * The first event in a NIP-10 root-following chain. When we follow e-tags
   * and the chain terminates at an unsupported (non-git) event, we show an
   * UnsupportedEventPage for this original event rather than the final root,
   * because that is what the user actually navigated to.
   */
  originalEvent?: NostrEvent;
}) {
  // Build relay groups: hint relays first, then git index, then extra relays
  const hintsKey = hintRelays.join(",");
  const searchGroups = useMemo<RelayGroupSpec[]>(() => {
    const groups: RelayGroupSpec[] = [];
    if (hintRelays.length > 0) {
      groups.push({ label: "hint relays", relays$: of(hintRelays) });
    }
    groups.push({ label: "git index", relays$: gitIndexRelays });
    groups.push({
      label: "fallback relays",
      relays$: fallbackRelays,
      deferred: true,
    });
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hintsKey]);

  const searchTarget = useMemo<SearchTarget>(
    () => ({ type: "event", id: eventId, authorPubkey }),
    [eventId, authorPubkey],
  );

  const search = useEventSearch(searchTarget, searchGroups);

  // Also subscribe to the store for the event (it may already be there or
  // arrive via the search)
  const event = use$(
    () =>
      eventStore.event(eventId) as unknown as Observable<
        NostrEvent | undefined
      >,
    [eventId],
  );

  // Delay showing the search status page so the plain spinner shows first for
  // up to 2s. Skip the delay if the search has already concluded (not found /
  // deleted / vanished) so we never sit on the spinner after the answer is known.
  const [searchDelayElapsed, setSearchDelayElapsed] = useState(false);
  useEffect(() => {
    setSearchDelayElapsed(false);
    const timer = setTimeout(() => setSearchDelayElapsed(true), 2000);
    return () => clearTimeout(timer);
  }, [eventId]);

  if (!event) {
    const searchConcluded =
      search && (search.concludedNotFound || search.deleted || search.vanished);
    if (search && (searchDelayElapsed || searchConcluded) && !search.found) {
      return (
        <EventSearchStatus
          search={search}
          eventId={eventId}
          itemLabel="Event"
        />
      );
    }
    return <LoadingState message="Fetching event…" />;
  }

  const kind = event.kind;

  // Repo announcement (kind 30617) — redirect to the repo landing page.
  // When arriving via a star reaction permalink, append ?stargazer=<pubkey>
  // so the StarButton can open the popover and highlight that user.
  if (kind === REPO_KIND) {
    const dTag = getReplaceableIdentifier(event);
    if (!dTag) return <NotFound />;
    const coord = `${REPO_KIND}:${event.pubkey}:${dTag}`;
    return (
      <RepoCoordRedirect
        coord={coord}
        hintRelays={hintRelays}
        subPath=""
        stargazerPubkey={stargazerPubkey}
      />
    );
  }

  if (kind === SOFTWARE_APPLICATION_KIND) {
    return (
      <SoftwareApplicationRedirect
        application={event}
        hintRelays={hintRelays}
      />
    );
  }

  if (kind === SOFTWARE_RELEASE_KIND) {
    return <SoftwareReleaseRedirect release={event} hintRelays={hintRelays} />;
  }

  if (kind === SOFTWARE_ASSET_KIND) {
    return <SoftwareAssetRedirect asset={event} hintRelays={hintRelays} />;
  }

  // Issue
  if (kind === ISSUE_KIND) {
    const coords = getRepoCoords(event);
    if (coords.length === 0) return <NotFound />;
    const repoRelays = dedupeRelays([
      ...getRepoCoordRelayHints(event),
      ...hintRelays,
    ]);
    const nevent = eventIdToNevent(eventId, hintRelays);
    const fragment = commentId ? `#${commentId.slice(0, 15)}` : "";
    return (
      <RepoCoordsRedirect
        coords={coords}
        hintRelays={repoRelays}
        subPath={`/issues/${nevent}${fragment}`}
      />
    );
  }

  // PR or root patch
  if (kind === PR_KIND || kind === PATCH_KIND) {
    const coords = getRepoCoords(event);
    if (coords.length === 0) return <NotFound />;
    const repoRelays = dedupeRelays([
      ...getRepoCoordRelayHints(event),
      ...hintRelays,
    ]);

    // Sub-patch: a kind:1617 that has an #e tag pointing to the root patch.
    // Redirect directly to /prs/<root-nevent>/commit/<sub-patch-nevent> so
    // the user lands on the root PR page with the sub-patch commit highlighted,
    // rather than treating the sub-patch as a standalone root PR.
    if (kind === PATCH_KIND) {
      const rootPatchId = event.tags.find(([t]) => t === "e")?.[1];
      if (rootPatchId) {
        const subPatchNevent = eventIdToNevent(eventId, hintRelays);
        const rootNevent = eventIdToNevent(rootPatchId, hintRelays);
        return (
          <RepoCoordsRedirect
            coords={coords}
            hintRelays={repoRelays}
            subPath={`/prs/${rootNevent}/commit/${subPatchNevent}`}
          />
        );
      }
    }

    const nevent = eventIdToNevent(eventId, hintRelays);
    const fragment = commentId ? `#${commentId.slice(0, 15)}` : "";
    return (
      <RepoCoordsRedirect
        coords={coords}
        hintRelays={repoRelays}
        subPath={`/prs/${nevent}${fragment}`}
      />
    );
  }

  // PR Update (kind 1619) — resolve the PR it belongs to and anchor to the
  // update event in the conversation tab, just like a NIP-22 comment.
  if (kind === PR_UPDATE_KIND) {
    const prRootId = event.tags.find(([t]) => t === "E")?.[1];
    if (!prRootId) return <NotFound />;
    return (
      <EventRedirect
        eventId={prRootId}
        hintRelays={hintRelays}
        commentId={event.id}
      />
    );
  }

  // NIP-22 comment (kind 1111) — resolve the root event
  if (kind === 1111) {
    const rootId = getCommentRootId(event);
    if (!rootId) return <NotFound />;
    // Recurse: resolve the root event, passing this comment's ID as the fragment
    return (
      <EventRedirect
        eventId={rootId}
        hintRelays={hintRelays}
        commentId={event.id}
      />
    );
  }

  // NIP-25 reaction (kind 7) — resolve the target event (last `e` tag per spec)
  if (kind === 7) {
    const eTags = event.tags.filter(([t]) => t === "e");
    const lastETag = eTags[eTags.length - 1];
    const targetId = lastETag?.[1];
    const targetRelay = lastETag?.[2];
    if (!targetId) return <NotFound />;
    const relays = targetRelay
      ? [targetRelay, ...hintRelays.filter((r) => r !== targetRelay)]
      : hintRelays;
    // Use the `k` tag to determine what was reacted to.
    const targetKind = parseInt(
      event.tags.find(([t]) => t === "k")?.[1] ?? "",
      10,
    );
    // Star on a repo announcement — pass the reactor's pubkey so the repo
    // page can open the stargazers popover and highlight them.
    if (targetKind === REPO_KIND) {
      return (
        <EventRedirect
          eventId={targetId}
          hintRelays={relays}
          stargazerPubkey={event.pubkey}
        />
      );
    }
    // Reaction on a comment — anchor to that comment in the thread.
    const resolvedCommentId = targetKind === 1111 ? targetId : undefined;
    return (
      <EventRedirect
        eventId={targetId}
        hintRelays={relays}
        commentId={resolvedCommentId}
      />
    );
  }

  // For any other kind, check if this event is threaded (NIP-10 `e` tags)
  // and the root is a supported git kind. If so, redirect to the root's page
  // with this event's ID as a fragment anchor (permalink to the comment).
  // Preserve commentId if already set (we may be resolving an intermediate
  // event in a chain — the original linked event is what we want to anchor to).
  //
  // We also carry the first event in the chain as `originalEvent` so that if
  // the chain terminates at a non-git (unsupported) event we can show an
  // UnsupportedEventPage for the event the user actually linked to, not the
  // unrelated social-media root.
  const nip10RootId = getNip10RootId(event);
  if (nip10RootId && nip10RootId !== eventId) {
    return (
      <EventRedirect
        eventId={nip10RootId}
        hintRelays={hintRelays}
        commentId={commentId ?? event.id}
        originalEvent={originalEvent ?? event}
      />
    );
  }

  // No thread root found or this IS the root.
  // If we got here by following a NIP-10 chain from an unsupported event,
  // show UnsupportedEventPage for the original event (the one the user linked
  // to), not the final root.  Otherwise show the current event.
  const pageEvent = originalEvent ?? event;
  return <UnsupportedEventPage event={pageEvent} relayHints={hintRelays} />;
}

// ---------------------------------------------------------------------------
// NIP-05 user page — resolves identity then renders UserPage
// ---------------------------------------------------------------------------

function Nip05UserPage({ nip05 }: { nip05: string }) {
  const identity = useDnsIdentity(nip05);

  if (identity.status === "loading") {
    return <LoadingState message={`Resolving ${nip05}…`} />;
  }

  if (identity.status === "not-found" || identity.status === "error") {
    return <NotFound />;
  }

  return <UserPage pubkey={identity.pubkey} />;
}

// ---------------------------------------------------------------------------
// Main NIP19Page
// ---------------------------------------------------------------------------

export function NIP19Page() {
  const { nip19: identifier } = useParams<{ nip19: string }>();

  if (!identifier) return <NotFound />;

  // Handle naddr1 — may point to a repo (kind 30617) or other addressable event
  if (identifier.startsWith("naddr1")) {
    try {
      const decoded = nip19.decode(identifier);
      if (decoded.type === "naddr") {
        const { kind, pubkey, identifier: dTag, relays } = decoded.data;
        if (kind === REPO_KIND) {
          return (
            <RepoPathRedirect
              pubkey={pubkey}
              repoId={dTag}
              relays={relays ?? []}
              subPath=""
            />
          );
        }
      }
    } catch {
      return <NotFound />;
    }
  }

  // Handle note1 / nevent1 — redirect to the appropriate issue/PR/comment page
  if (identifier.startsWith("note1") || identifier.startsWith("nevent1")) {
    try {
      const decoded = nip19.decode(identifier);
      let eventId: string;
      let hintRelays: string[] = [];
      let authorPubkey: string | undefined;

      if (decoded.type === "note") {
        eventId = decoded.data;
      } else if (decoded.type === "nevent") {
        eventId = decoded.data.id;
        hintRelays = decoded.data.relays ?? [];
        authorPubkey = decoded.data.author;
      } else {
        return <NotFound />;
      }

      return (
        <EventRedirect
          eventId={eventId}
          hintRelays={hintRelays}
          authorPubkey={authorPubkey}
        />
      );
    } catch {
      return <NotFound />;
    }
  }

  // Get the user pointer from the identifier
  const user = normalizeToProfilePointer(identifier);

  // Attempt to get the event pointer
  const pointer =
    normalizeToAddressPointer(identifier) ||
    normalizeToEventPointer(identifier);

  // Load the event from the relays
  const event = use$(
    () => (pointer ? eventStore.event(pointer) : undefined),
    [JSON.stringify(pointer)],
  );

  if (identifier.startsWith("npub1") || identifier.startsWith("nprofile1")) {
    if (!user) return <NotFound />;

    return <UserPage pubkey={user.pubkey} />;
  } else if (isHexPubkey(identifier)) {
    return <UserPage pubkey={identifier.toLowerCase()} />;
  } else if (pointer) {
    if (!event) return <LoadingState message="Fetching event…" />;

    if (event.kind === SOFTWARE_APPLICATION_KIND) {
      const hintRelays = "relays" in pointer ? (pointer.relays ?? []) : [];
      return (
        <SoftwareApplicationRedirect
          application={event}
          hintRelays={hintRelays}
        />
      );
    }

    // For any event kind this app doesn't have a dedicated page for, show a
    // preview with a link to njump.me so users aren't left with a blank 404.
    const hintRelays = "relays" in pointer ? (pointer.relays ?? []) : [];
    return <UnsupportedEventPage event={event} relayHints={hintRelays} />;
  } else if (isNip05(identifier)) {
    // Bare domain (danconwaydev.com) or _@domain.com — resolve to user page
    return <Nip05UserPage nip05={standardizeNip05(identifier)} />;
  } else {
    return <NotFound />;
  }
}
